import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, StyleSheet, Text, TextInput,
} from 'react-native';
import Colors from '../../../../constants/Colors';
import Style from '../../../../constants/Style';
import {
  BuildingSettingsPriority, constrainMinutes, convertHours, pad,
} from './data';

interface PriorityItemProps extends BuildingSettingsPriority {
  index: number,
  setName: Dispatch<SetStateAction<string>>,
  setResponseHour: Function,
  setResponseMinute: Function,
  setResolutionHour: Function,
  setResolutionMinute: Function,
  setDescription: Dispatch<SetStateAction<string>>,
}

const PriorityItem = ({
  Type, Name, ResponseHours, ResponseMinutes, ResolutionHours, ResolutionMinutes, Description,
  setResponseHour, setName, setResponseMinute, setResolutionHour, setResolutionMinute, index,
  setDescription,
}: PriorityItemProps) => {
  const [responseMinutesText, setResponseMinutesText] = useState<string>(pad(ResponseMinutes));
  const [resolutionMinutesText,
    setResolutionMinutesText] = useState<string>(pad(ResolutionMinutes));

  return <View style={[Style.row, styles.item]}>
    <Text style={[styles.title, styles.indexColumn]}>{Type}</Text>
    <View style={styles.nameColumn}>
      <TextInput
        style={[styles.input, styles.nameField, styles.text]}
        value={Name}
        onChangeText={setName}
        testID={`lm-p${index + 1}-priority-name-input-field`}
      />
    </View>
    <View style={[Style.row, styles.hoursColumn, styles.timeInputContainer]}>
      <TextInput
        value={ResponseHours.toString()}
        onChangeText={(text: string) => setResponseHour(convertHours(text))}
        maxLength={3}
        style={[styles.input, styles.timeField, styles.text]}
        testID={`lm-p${index + 1}-response-hours-input-field`}
      />
      <Text style={[styles.text, styles.colon]}>:</Text>
      <TextInput
        value={responseMinutesText}
        onChangeText={(text: string) => setResponseMinutesText(constrainMinutes(text).toString())}
        onBlur={() => {
          setResponseMinute(parseInt(responseMinutesText, 10));
          setResponseMinutesText(pad(parseInt(responseMinutesText, 10)));
        }}
        maxLength={2}
        style={[styles.input, styles.timeField, styles.text]}
        testID={`lm-p${index + 1}-response-minutes-input-field`}
      />
    </View>
    <View style={[Style.row, styles.hoursColumn, styles.timeInputContainer]}>
      <TextInput
        value={ResolutionHours.toString()}
        onChangeText={(text: string) => setResolutionHour(convertHours(text))}
        maxLength={3}
        style={[styles.input, styles.timeField, styles.text]}
        testID={`lm-p${index + 1}-resolution-hours-input-field`}
      />
      <Text style={[styles.text, styles.colon]}>:</Text>
      <TextInput
        value={resolutionMinutesText}
        onChangeText={(text: string) => setResolutionMinutesText(constrainMinutes(text).toString())}
        onBlur={() => {
          setResolutionMinute(parseInt(resolutionMinutesText, 10));
          setResolutionMinutesText(pad(parseInt(resolutionMinutesText, 10)));
        }}
        maxLength={2}
        style={[styles.input, styles.timeField, styles.text]}
        testID={`lm-p${index + 1}-resolution-minutes-input-field`}
      />
    </View>
    <TextInput
      value={Description}
      onChangeText={setDescription}
      multiline
      style={[styles.input, styles.text, styles.descriptionColumn, styles.descriptionField]}
      testID={`lm-p${index + 1}-priority-description-input-field`}
    />
  </View>;
};

interface PrioritySettingsProps {
  priorityList: BuildingSettingsPriority[],
  setPriorityList: Dispatch<SetStateAction<BuildingSettingsPriority[]>>,
}

const PrioritySettings = ({ priorityList, setPriorityList }: PrioritySettingsProps) => <>
  <View style={[Style.row, styles.header]}>
    <Text style={[styles.parameterTitle, styles.indexColumn]}>/</Text>
    <Text style={[styles.parameterTitle, styles.nameColumn]}>{'PRIORITY NAME'}</Text>
    <Text style={[styles.parameterTitle, styles.hoursColumn]}>{'RESPONSE HOURS'}</Text>
    <Text style={[styles.parameterTitle, styles.hoursColumn]}>{'RESOLUTION HOURS'}</Text>
    <Text style={[styles.parameterTitle, styles.descriptionColumn]}>
      {'PRIORITY DESCRIPTION'}
    </Text>
  </View>
  {priorityList.map((priority: BuildingSettingsPriority, index: number) => <PriorityItem
    key={`priority_item_${index}`}
    {...{ ...priority, index }}
    setName={(text: string) => {
      const updatedList: BuildingSettingsPriority[] = [...priorityList];
      updatedList[index].Name = text;
      setPriorityList(updatedList);
    }}
    setResponseHour={(value: number) => {
      const updatedList: BuildingSettingsPriority[] = [...priorityList];
      updatedList[index].ResponseHours = value;
      setPriorityList(updatedList);
    }}
    setResponseMinute={(value: number) => {
      const updatedList: BuildingSettingsPriority[] = [...priorityList];
      updatedList[index].ResponseMinutes = value;
      setPriorityList(updatedList);
    }}
    setResolutionHour={(value: number) => {
      const updatedList: BuildingSettingsPriority[] = [...priorityList];
      updatedList[index].ResolutionHours = value;
      setPriorityList(updatedList);
    }}
    setResolutionMinute={(value: number) => {
      const updatedList: BuildingSettingsPriority[] = [...priorityList];
      updatedList[index].ResolutionMinutes = value;
      setPriorityList(updatedList);
    }}
    setDescription={(text: string) => {
      const updatedList: BuildingSettingsPriority[] = [...priorityList];
      updatedList[index].Description = text;
      setPriorityList(updatedList);
    }}
  />)}
</>;

const styles = StyleSheet.create({
  header: {
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksWhite500,
    paddingHorizontal: 16,
  },
  parameterTitle: {
    fontSize: 10,
    lineHeight: 15,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
  },
  indexColumn: { flex: 0.05 },
  nameColumn: { flex: 0.24 },
  hoursColumn: { flex: 0.15 },
  descriptionColumn: { flex: 0.41 },
  item: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: Colors.light.hereworksWhite200,
    borderRadius: 5,
    marginTop: 8,
    maxHeight: 90,
  },
  title: {
    fontSize: 20,
    lineHeight: 30,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  timeInputContainer: {
    justifyContent: 'flex-start',
  },
  text: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  colon: { marginHorizontal: 4 },
  input: {
    paddingHorizontal: 8,
    paddingVertical: 10,
    backgroundColor: '#FFF',
  },
  nameField: {
    width: 136,
    height: 44,
  },
  timeField: {
    width: 44,
    aspectRatio: 1,
    textAlign: 'center',
  },
  descriptionField: {
    minHeight: 44,
  },
});

export default PrioritySettings;
